import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, Target, Plus, Sparkles, ArrowRight, BatteryLow, BatteryMedium, BatteryFull } from 'lucide-react';
import LoadingSpinner from './ui/LoadingSpinner';
import EmptyState from './ui/EmptyState.jsx';
import Button from './ui/Button';
import Card from './ui/Card.jsx';
import ChoiceGroup from './ui/ChoiceGroup.jsx';
import FormField from './ui/FormField.jsx';
import StarRating from './ui/StarRating.jsx';
import Input, { Textarea } from './ui/Input.jsx';
import PropTypes from 'prop-types';
import { apiService } from '../services/api';
import { toast } from '../components/notifications';


const SessionFeedback = ({ session, onSubmit, onCancel, coachUsed = false, initialFeedback = null }) => {
  // initialFeedback: bestehendes Feedback zum Bearbeiten (Lernlogbuch 2.0)
  const [rating, setRating] = useState(initialFeedback?.rating || 0);
  const [feedback, setFeedback] = useState(initialFeedback?.comment || '');
  const [learningResults, setLearningResults] = useState(initialFeedback?.learningResults || '');
  const [reflection, setReflection] = useState(initialFeedback?.reflection || '');
  const [nextSteps, setNextSteps] = useState(initialFeedback?.nextSteps || '');
  const [energyLevel, setEnergyLevel] = useState(initialFeedback?.energyLevel || '');
  const [isSubmitting, setIsSubmitting] = useState(false); // CRITICAL: Prevent double-submit
  const [goals, setGoals] = useState(session.goals || []);
  const [newGoal, setNewGoal] = useState('');
  const [skillVerifications] = useState(
    session.participants.map(p => ({
      userId: p.id,
      skills: [],
      verified: false
    }))
  );

  // Warnung beim echten Tab-Schliessen/Reload NUR wenn bereits etwas eingegeben wurde.
  // (beforeunload greift nicht bei In-App-Navigation; ohne Inhalt nicht blockieren,
  //  damit Nutzer - gerade am Smartphone - nicht grundlos festhaengen.)
  const hasUnsavedInput = rating > 0
    || feedback.trim() !== ''
    || reflection.trim() !== ''
    || nextSteps.trim() !== ''
    || learningResults.trim() !== ''
    || energyLevel !== '';

  // ✨ KI-Vorschlag (Lernlogbuch 2.0): TeachOtter-Entwurf laden und leere Felder vorbefuellen.
  // Bei coachUsed wird gepollt (die KI braucht nach Session-Ende 1-3s), sonst nur einmal
  // geprueft (z.B. nachtraegliches Feedback nach Auto-Complete, Entwurf liegt schon vor).
  const [isAiPrefilled, setIsAiPrefilled] = useState(false);
  const hasUnsavedInputRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const maxAttempts = coachUsed ? 8 : 1;

    const applyDraft = (aiDraft) => {
      if (!aiDraft || (!aiDraft.learningResults && !aiDraft.reflection && !aiDraft.nextSteps)) {
        return false;
      }
      // Nur leere Felder befuellen - nie Eingaben des Schuelers ueberschreiben
      setLearningResults(prev => prev || aiDraft.learningResults || '');
      setReflection(prev => prev || aiDraft.reflection || '');
      setNextSteps(prev => prev || aiDraft.nextSteps || '');
      setIsAiPrefilled(true);
      return true;
    };

    const poll = async () => {
      attempts += 1;
      try {
        const response = await apiService.getSessionFeedbackDraft(session.id);
        if (cancelled) return;
        if (applyDraft(response?.data?.aiDraft)) return;
      } catch (error) {
        // 404 = noch kein Entwurf vorhanden - weiter pollen
      }
      if (!cancelled && attempts < maxAttempts && !hasUnsavedInputRef.current) {
        timer = setTimeout(poll, 2000);
      }
    };

    if (coachUsed) {
      // KI-Generierung aktiv anstossen (Session kann noch ACTIVE sein - der
      // Abschluss passiert erst beim Absenden). Antwort enthaelt den Entwurf
      // direkt; der Poll dient nur als Fallback.
      apiService.generateSessionFeedbackDraft(session.id)
        .then((response) => {
          if (cancelled) return;
          if (!applyDraft(response?.data?.aiDraft)) poll();
        })
        .catch(() => {
          if (!cancelled) poll();
        });
    } else {
      poll();
    }
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id, coachUsed]);

  // Ref fuer den Draft-Poll: sobald der Schueler selbst etwas eingibt, nicht weiter pollen
  useEffect(() => {
    hasUnsavedInputRef.current = hasUnsavedInput;
  }, [hasUnsavedInput]);

  useEffect(() => {
    if (!hasUnsavedInput) return undefined;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Seite verlassen? Dein Feedback ist noch nicht gespeichert.';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedInput]);

  const addGoal = () => {
    if (newGoal.trim()) {
      const goal = {
        id: Date.now(), // Temporary ID
        text: newGoal.trim(),
        completed: false
      };
      setGoals(prev => [...prev, goal]);
      setNewGoal('');
    }
  };

  // Drei-Wege-Aufloesung (Lernlogbuch 2.0): Was passiert mit dem Ziel?
  // 'completed' = geschafft | 'carry_over' = spaeter weitermachen | 'make_long_term' = Langzeitziel
  const setGoalResolution = (goalId, resolution) => {
    setGoals(prev => prev.map(goal =>
      goal.id === goalId
        ? { ...goal, resolution, completed: resolution === 'completed' }
        : goal
    ));
  };

  const removeGoal = (goalId) => {
    setGoals(prev => prev.filter(goal => goal.id !== goalId));
  };

  const handleSubmit = async () => {
    // CRITICAL: Prevent double-submit
    if (isSubmitting) {
      console.log('⚠️ SessionFeedback: Already submitting, ignoring duplicate call');
      return;
    }

    // ✅ VALIDATION: User muss mindestens Rating ODER einen Text-Kommentar abgeben
    const hasRating = rating > 0;
    const hasTextFeedback = feedback.trim() || learningResults.trim() || reflection.trim();

    if (!hasRating && !hasTextFeedback) {
      toast.error('Vergib Sterne oder schreib kurz, was du gelernt hast.');
      return;
    }

    // ENTFERNT: Zwangs-Kommentar bei schlechten Bewertungen - nicht sinnvoll

    setIsSubmitting(true);

    // Prepare feedback data
    const feedbackData = {
      rating,
      comment: feedback,
      learningResults,
      reflection,
      nextSteps,
      energyLevel,
      goals,
      skillVerifications,
    };

    // Pass the feedback data to parent (bewusst ohne console.log: Ziel- und
    // Kommentar-Texte sind Nutzerinhalt und gehören nicht in die Konsole)
    try {
      await onSubmit(feedbackData);
    } catch (error) {
      // Fehler-Toast kommt bereits aus useSessionActions.submitFeedback
      console.error('Fehler beim Absenden des Feedbacks:', error);
      setIsSubmitting(false); // Reset on error
    }
  };

  // Der frueher hier definierte FocusBonusSection-Block ist entfallen: er wurde
  // nirgends gerendert und warb mit einem Fokus-Bonus, den es nicht mehr gibt.

  return (
    <Card
      className="overflow-hidden"
      style={{ paddingTop: 'var(--native-safe-area-top, env(safe-area-inset-top, 0px))' }}
    >
      <div className="p-6">
        <h3 className="text-xl font-bold text-foreground mb-6">Session Feedback</h3>

        <div className="space-y-6">
          {/* Bewertung — derselbe Baustein wie im Logbuch-Dialog (ui/StarRating),
              damit beide Formulare gleich aussehen und gleich zu bedienen sind.
              Die Frage ist hier laenger als "Wie lief es?", deshalb stehen die
              Sterne darunter statt daneben (in einer Zeile braechen sie am Telefon
              ohnehin um). */}
          <div className="space-y-1">
            <span id="rating-label" className="block font-medium text-foreground-2">
              Wie war die Session?
            </span>
            <StarRating value={rating} onChange={setRating} labelledBy="rating-label" />
          </div>

          {/* Learning Goals */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="flex items-center gap-2 font-medium text-foreground-2">
                <Target className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                Lernziele
              </h4>
              <span className="text-sm text-muted-foreground">
                {goals.filter(g => g.completed).length} von {goals.length} erreicht
              </span>
            </div>

            {/* Existing Goals - Drei-Wege-Aufloesung (Lernlogbuch 2.0) */}
            {goals.length > 0 && (
              <div className="space-y-2">
                {goals.map((goal) => {
                  const resolution = goal.resolution || (goal.completed ? 'completed' : null);
                  return (
                    <div key={goal.id} className="p-3 bg-surface-raised rounded-lg space-y-2">
                      <div className="flex items-center gap-3">
                        <span className={`flex-1 text-sm sm:text-base ${resolution === 'completed' ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                          {goal.text}
                        </span>
                        <button
                          onClick={() => removeGoal(goal.id)}
                          className="p-1 hover:bg-foreground/10 rounded-full flex-shrink-0"
                          aria-label="Ziel entfernen"
                        >
                          <X className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                        </button>
                      </div>
                      {/* Drei Wege, die einander ausschliessen — also eine Radiogruppe.
                          Keine Farben: die drei sind gleichwertig, da bedeutet Farbe nichts. */}
                      <ChoiceGroup
                        label={`Was ist mit dem Ziel „${goal.text}" passiert?`}
                        value={resolution}
                        onChange={(next) => setGoalResolution(goal.id, next)}
                        options={[
                          { value: 'completed', label: 'Geschafft', icon: CheckCircle },
                          { value: 'carry_over', label: 'Später weiter', icon: ArrowRight },
                          { value: 'make_long_term', label: 'Langzeitziel', icon: Target },
                        ]}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add New Goal */}
            <div className="flex gap-2">
              <Input
                type="text"
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addGoal()}
                placeholder="Neues Lernziel hinzufügen..."
                aria-label="Neues Lernziel"
                enterKeyHint="done"
                className="flex-1"
              />
              <Button
                onClick={addGoal}
                disabled={!newGoal.trim()}
                className="shrink-0"
                aria-label="Lernziel hinzufügen"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>

            {goals.length === 0 && (
              <EmptyState
                size="sm"
                icon={Target}
                title="Noch keine Lernziele definiert"
                text="Füge Ziele hinzu, um deinen Fortschritt zu verfolgen"
              />
            )}
          </div>

          {/* KI-Vorschlag Hinweis */}
          {isAiPrefilled && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-surface-raised border border-border animate-fade-in">
              <Sparkles className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Von TeachOtter vorgeschlagen</span> — lies den Entwurf und passe ihn an, bevor du ihn speicherst.
              </p>
            </div>
          )}

          {/* Learning Results */}
          <FormField
            label={(
              <span className="flex items-center gap-2">
                Was hast du gelernt?
                {isAiPrefilled && learningResults && <Sparkles className="h-4 w-4 text-faint" aria-hidden="true" />}
              </span>
            )}
          >
            <Textarea
              value={learningResults}
              onChange={(e) => setLearningResults(e.target.value)}
              placeholder="Was hast du in dieser Session gelernt oder geübt?"
              rows={3}
            />
          </FormField>

          {/* Reflection */}
          <FormField
            label={(
              <span className="flex items-center gap-2">
                Reflexion
                {isAiPrefilled && reflection && <Sparkles className="h-4 w-4 text-faint" aria-hidden="true" />}
              </span>
            )}
          >
            <Textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="Wie war die Session für dich? Was ist gut gelaufen, was könnte besser werden?"
              rows={3}
            />
          </FormField>

          {/* Energy Level — hier tragen die Farben Bedeutung (wenig/mittel/viel),
              deshalb bleibt der rote/gelbe/gruene Rand. */}
          <div className="space-y-2">
            <span className="block font-medium text-foreground-2">
              Wie war dein Energielevel?
            </span>
            <ChoiceGroup
              label="Wie war dein Energielevel?"
              value={energyLevel}
              onChange={setEnergyLevel}
              options={[
                { value: 'niedrig', label: 'Niedrig', icon: BatteryLow, tone: 'danger' },
                { value: 'mittel', label: 'Mittel', icon: BatteryMedium, tone: 'warning' },
                { value: 'hoch', label: 'Hoch', icon: BatteryFull, tone: 'success' },
              ]}
            />
          </div>

          {/* Next Steps */}
          <FormField
            label={(
              <span className="flex items-center gap-2">
                Nächste Schritte
                {isAiPrefilled && nextSteps && <Sparkles className="h-4 w-4 text-faint" aria-hidden="true" />}
              </span>
            )}
          >
            <Textarea
              value={nextSteps}
              onChange={(e) => setNextSteps(e.target.value)}
              placeholder="Was möchtest du als nächstes lernen oder vertiefen?"
              rows={2}
            />
          </FormField>



          {/* Feedback Text - Hidden, aber State bleibt für Validierung */}
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
            aria-label="Internes Feedback-Feld"
          />
        </div>
      </div>

      {/* Aktions-Buttons
          Der Absende-Knopf lief bis August 2026 ueber SharedStyles.buttonMagicSubtle -
          einen zarten Violett-Blau-Verlauf. Zwei festgehaltene Regeln stehen dem
          entgegen (TOKENS.md): Verlaeufe in CTAs sind untersagt, und dieser Knopf
          ist ausdruecklich der Landingpage vorbehalten, weil er auf hellem
          Seitenhintergrund kaum als Hauptaktion zu erkennen ist. */}
      <div className="border-t border-border p-4 bg-surface-raised">
        <div className="flex gap-3">
          {/* "Später bewerten" Button nur wenn onCancel übergeben wurde (optional) */}
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} className="flex-1">
              Später bewerten
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={onCancel ? 'flex-1' : 'w-full'}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="small" />
                <span>Wird gesendet...</span>
              </>
            ) : (
              <>
                <span>{initialFeedback ? 'Speichern' : 'Feedback senden'}</span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};

SessionFeedback.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    topic: PropTypes.string.isRequired,
    duration: PropTypes.number,
    auraEarned: PropTypes.number.isRequired,
    participants: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      skills: PropTypes.arrayOf(PropTypes.string),
      isHost: PropTypes.bool,
      level: PropTypes.number
    })).isRequired,
    host: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      level: PropTypes.number.isRequired
    }).isRequired,
    focusStats: PropTypes.shape({
      focusRatio: PropTypes.number.isRequired
    }),
    verifiedBy: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      level: PropTypes.number.isRequired,
      timestamp: PropTypes.string.isRequired
    }))
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,  // Optional - wenn nicht übergeben, kann Modal nicht geschlossen werden
  coachUsed: PropTypes.bool,  // true = TeachOtter wurde genutzt -> auf KI-Vorschlag pollen
  initialFeedback: PropTypes.object  // Bestehendes Feedback zum Bearbeiten (Lernlogbuch 2.0)
};

export default SessionFeedback;
